import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, Loader2, RefreshCw, Settings2, WifiOff } from 'lucide-react';
import { ApiError, errorMessage } from '../../lib/api';
import { prerequisiteInfo } from '../../lib/prereq';
import { Button } from './Button';

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <span role="status" className="inline-flex items-center">
      <Loader2 className={clsx('h-5 w-5 animate-spin text-primary', className)} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton', className)} aria-hidden />;
}

export function SkeletonLines({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={clsx('space-y-2', className)} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={clsx('h-3.5', i === lines - 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  );
}

export function PageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center">
      <Spinner className="h-7 w-7" label={label} />
    </div>
  );
}

export function EmptyState({ icon, title, description, actions, className }: { icon?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex flex-col items-center justify-center px-6 py-10 text-center', className)}>
      {icon && <div className="mp-pill-lavender mb-3 flex h-14 w-14 items-center justify-center rounded-2xl">{icon}</div>}
      <h3 className="text-[0.9375rem] font-semibold text-ink">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-[0.8125rem] text-muted">{description}</p>}
      {actions && <div className="mt-4 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}

/** Setup-needed notice for 412 prerequisite_missing — never a dead control. */
export function PrerequisiteNotice({ error, compact }: { error: unknown; compact?: boolean }) {
  const p = prerequisiteInfo(error);
  if (!p) return null;
  return (
    <div role="alert" className={clsx('flex items-start gap-3 rounded-2xl border border-warn/40 bg-warn-soft text-warn-ink', compact ? 'p-3' : 'p-4')}>
      <Settings2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0">
        <p className="font-semibold">{p.title}</p>
        <p className="mt-0.5 text-[0.8125rem]">{p.message}</p>
        <Link to={p.to} className="mt-1.5 inline-block text-[0.8125rem] font-semibold underline underline-offset-2">
          {p.linkLabel} →
        </Link>
      </div>
    </div>
  );
}

/** Recoverable error with retry (and prerequisite guidance when applicable). */
export function ErrorState({ error, onRetry, title = "Couldn't load this", compact }: { error: unknown; onRetry?: () => void; title?: string; compact?: boolean }) {
  if (prerequisiteInfo(error)) return <PrerequisiteNotice error={error} compact={compact} />;
  const offline = error instanceof ApiError && error.isNetwork;
  return (
    <div role="alert" className={clsx('flex flex-col items-center justify-center text-center', compact ? 'gap-2 p-4' : 'gap-3 px-6 py-10')}>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-danger-soft text-danger-ink">{offline ? <WifiOff className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}</div>
      <div>
        <p className="font-semibold text-ink">{title}</p>
        <p className="mt-0.5 text-[0.8125rem] text-muted">{errorMessage(error)}</p>
      </div>
      {onRetry && (
        <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  if (prerequisiteInfo(error)) return <PrerequisiteNotice error={error} compact />;
  return (
    <p role="alert" className="rounded-xl bg-danger-soft px-3 py-2 text-[0.8125rem] text-danger-ink">
      {errorMessage(error)}
    </p>
  );
}

export function Notice({ tone = 'info', icon, children, className }: { tone?: 'info' | 'warn' | 'danger' | 'success'; icon?: ReactNode; children: ReactNode; className?: string }) {
  const t = {
    info: 'bg-info-soft text-info-ink border-info/30',
    warn: 'bg-warn-soft text-warn-ink border-warn/40',
    danger: 'bg-danger-soft text-danger-ink border-danger/40',
    success: 'bg-success-soft text-success-ink border-success/30',
  }[tone];
  return (
    <div className={clsx('flex items-start gap-2.5 rounded-2xl border px-3.5 py-3 text-[0.8125rem]', t, className)}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0">{children}</div>
    </div>
  );
}
